import * as React from "react";

import "./styles.scss";
import {
  ButtonTypes,
  CardQuestion,
  CommonContent,
  CommonContentType,
  Content,
  ContentKey,
  QuestionType,
  RatingLabels,
} from "../../types/Types";
import {
  GameDetails,
  GamePositionColor,
  Player,
  PlayerDetails,
  UserAccount,
} from "../../types/Game";
import YelloCard, { YelloCardProps } from "./../YellowCard";
import GenericContent from "../../services/GenericContent";
import RedCard, { ReadCardProps } from "../RedCard";
import BlueCard, { BlueCardProps } from "../BlueCard";
import { API_URL } from "../../config/Constants";
import GreenCard, { GreenCardDataProps } from "../GreenCard";
import PlayerService from "../../services/PlayerService";

type Props = {
  content: Content[];
  players: Player[];
  commonContent: CommonContent[];
  cardColor: GamePositionColor;
  cardContent: Content;
  onTimeOut: () => void;
  isUsersTurn: boolean;
  onCardAnswer: (answer: string, isRight: boolean) => void;
  onGreenCardAnswer?: (answer: string) => void;
  onGreenCardTyping?: (answer: string) => void;
  onUseMagnifyingGlass: () => void;
  magnifyingGlasses: number;
  game: GameDetails;
  user: UserAccount;
};

export default function GameCard(props: Props) {
  const [yellowCard, setYellowCard] = React.useState<YelloCardProps | null>(
    null
  );
  const [redCard, setRedCard] = React.useState<ReadCardProps | null>(null);
  const [blueCard, setBlueCard] = React.useState<BlueCardProps | null>(null);
  const [greenCard, setGreenCard] = React.useState<GreenCardDataProps | null>(
    null
  );
  const [cardQuestions, setCardQuestions] = React.useState<CardQuestion[]>();

  React.useEffect(() => {
    console.log("Card color:", props.cardColor);
    console.log("Cart content Item:", props.cardContent);

    if (props.cardColor === GamePositionColor.Yellow) {
      setUpYellowCard();
      return;
    }
    if (props.cardColor === GamePositionColor.Red) {
      createQuestions(CommonContentType.RED_CARD_ANSWER);
      setUpRedCard();
      return;
    }

    if (props.cardColor === GamePositionColor.Blue) {
      createQuestions(CommonContentType.BLUE_CARD_ANSWER);
      setUpBlueCard();
      return;
    }

    if (props.cardColor === GamePositionColor.Green) {
      setUpGreenCard();
      return;
    }
  }, [props.cardContent]);

  const getTimeoutValue = (card: Content): number => {
    return card.timeOut ? Number(card.timeOut) : 30;
  };

  const getCardAnswer = (card: Content) => {
    return card.questionType === "true_false"
      ? card.cardAnswer
      : card.rightCardAnswer;
  };

  const setUpYellowCard = () => {
    setYellowCard({
      timeOut: getTimeoutValue(props.cardContent),
      title: props.cardContent?.title,
      body: props.cardContent?.description || "",
      author: GenericContent.getNameById(
        props.commonContent,
        props.cardContent.authorQuote || ""
      ),
      display: true,
      continueLabel: GenericContent.getButtonName(
        props.commonContent,
        ButtonTypes.CONTINUE
      ),
    });
  };

  const setUpRedCard = () => {
    const continuePlayingLabel = GenericContent.getButtonName(
      props.commonContent,
      ButtonTypes.CONTINUE
    );
    const hintLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.HINT_LABEL
    );

    const rightAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.RIGHT_ANSWER
    );

    const sendAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.SEND_ANSWER_LABEL
    );

    const wrongAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.WRONG_ANSWER
    );

    setRedCard({
      magnifyingGlasses: props.magnifyingGlasses,
      timeOut: getTimeoutValue(props.cardContent),
      title: props.cardContent?.title,
      body: props.cardContent?.description || "",
      subTitle: props.cardContent.questionSituation || "",
      hint: props.cardContent.answerHint || "",
      hiddenAnswer: getHiddenAnswerUrl(props.cardContent.hiddenAnswer),
      hintLabel,
      rightAnswerLabel,
      rightAnswerValue: getCardAnswer(props.cardContent) || "",
      wrongAnswerLabel,
      continuePlayingLabel,
      sendAnswerLabel,
    });
  };

  const setUpBlueCard = () => {
    const continuePlayingLabel = GenericContent.getButtonName(
      props.commonContent,
      ButtonTypes.CONTINUE
    );
    const hintLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.HINT_LABEL
    );

    const rightAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.RIGHT_ANSWER
    );

    const sendAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.SEND_ANSWER_LABEL
    );
    const wrongAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.WRONG_ANSWER
    );

    setBlueCard({
      magnifyingGlasses: props.magnifyingGlasses,
      timeOut: getTimeoutValue(props.cardContent),
      title: props.cardContent?.title,
      body: props.cardContent?.description || "",
      subTitle: props.cardContent.questionSituation || "",
      hint: props.cardContent.answerHint || "",
      hiddenAnswer: getHiddenAnswerUrl(props.cardContent.hiddenAnswer),
      hintLabel,
      rightAnswerLabel,
      continuePlayingLabel,
      wrongAnswerLabel,
      sendAnswerLabel,
      rightAnswerValue: getCardAnswer(props.cardContent) || "",
    });
  };

  const setUpGreenCard = () => {
    const continuePlayingLabel = GenericContent.getButtonName(
      props.commonContent,
      ButtonTypes.CONTINUE
    );

    const sendAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.SEND_ANSWER_LABEL
    );

    const rightAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.RIGHT_ANSWER
    );
    const wrongAnswerLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.WRONG_ANSWER
    );
    const timeoutLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.TIME_OUT
    );

    const finalEvaluation = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.FINAL_EVALUATION
    );

    const sendAnLabel = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.SEND_ANSWER_LABEL
    );

    setGreenCard({
      timeOut: getTimeoutValue(props.cardContent),
      title: props.cardContent.title || "",
      body: props.cardContent?.description || "",
      continuePlayingLabel,
      sendAnswerLabel,
      rightAnswerLabel,
      timeoutLabel,
      wrongAnswerLabel,
      finalEvaluation,
      sendAnLabel,
    });
  };

  const createRatingLabels = (): RatingLabels => {
    const ok = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.OK_LABEL
    );

    const excellent = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.EXCELLENT_LABEL
    );

    const poor = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.POOR_LABEL
    );

    const useless = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.USELESS_LABEL
    );

    const good = GenericContent.getKeyValue(
      props.commonContent,
      ContentKey.GOOD_LABEL
    );

    return { ok, excellent, poor, useless, good };
  };

  const createQuestions = (type: CommonContentType) => {
    if (props.cardContent?.questionType === QuestionType.MULTIPLE_OPTIONS) {
      const questions = GenericContent.getAllCardsQuestions(
        props.commonContent,
        type
      );
      console.log("Question:", props.cardContent);
      console.log("questions:", questions);

      setCardQuestions(
        GenericContent.getCardQuestions(
          questions,
          props.cardContent.cardAnswers || []
        )
      );
    } else {
      const verdadero = GenericContent.getKeyValue(
        props.commonContent,
        ContentKey.TRUE_LABEL
      );

      const falso = GenericContent.getKeyValue(
        props.commonContent,
        ContentKey.FALSE_LABEL
      );

      setCardQuestions([
        { id: "0", label: falso },
        { id: "1", label: verdadero },
      ]);
    }
  };

  const getHiddenAnswerUrl = (hiddenAnswerImg: string) => {
    return hiddenAnswerImg ? `${API_URL}${hiddenAnswerImg}` : "";
  };

  const onGreenCardAnswer = (answers: string) => {
    if (!props.onGreenCardAnswer) {
      console.log("onGreenCardAnswer is undefined");
      return;
    }
    props.onGreenCardAnswer(answers);
  };

  const onGreenCardTyping = (answers: string) => {
    if (!props.onGreenCardTyping) {
      console.log("onGreenCardAnswer is undefined");
      return;
    }
    props.onGreenCardTyping(answers);
  };

  return (
    <div>
      {yellowCard && (
        <YelloCard
          data={yellowCard}
          isUsersTurn={props.isUsersTurn}
          onTimeOut={props.onTimeOut}
        />
      )}
      {greenCard && (
        <GreenCard
          content={props.commonContent}
          players={props.players}
          card={props.cardContent}
          user={props.user}
          onCardAnswer={onGreenCardAnswer}
          onTyping={(res) => {
            onGreenCardTyping(res);
          }}
          isUsersTurn={props.isUsersTurn}
          onTimeOut={props.onTimeOut}
          data={greenCard}
          ratingLabels={createRatingLabels()}
          reviewerMode={true}
          game={props.game}
        />
      )}
      {redCard && (
        <RedCard
          onCardAnswer={props.onCardAnswer}
          data={redCard}
          isUsersTurn={props.isUsersTurn}
          onTimeOut={props.onTimeOut}
          questions={cardQuestions || []}
          game={props.game}
          onUseMagnifyingGlass={props.onUseMagnifyingGlass}
        />
      )}
      {blueCard && (
        <BlueCard
          onUseMagnifyingGlass={props.onUseMagnifyingGlass}
          onCardAnswer={props.onCardAnswer}
          isUsersTurn={props.isUsersTurn}
          onTimeOut={props.onTimeOut}
          data={blueCard}
          questions={cardQuestions || []}
          game={props.game}
        />
      )}
    </div>
  );
}
